import { MDBContainer } from "mdb-react-ui-kit";
import {
    FC,
    ReactNode,
    useEffect,
    useState,
    Dispatch,
    SetStateAction,
} from "react";
import { useStore } from "@/store/index";
import {
    getNewsletterFormService,
    getSubscribeNewsletter,
} from "@/utils/queries";
import { useQuery } from "@tanstack/react-query";
import { useRouter } from "next/router";
import { ENewsletterTriggerOption } from "@/models/newsletter";
import FooterLayout from "./footer/FooterLayout";
import HeadMeta from "./HeadMeta";
import NavBarLayout from "./NavBar/NavBarLayout";
import ErrorBoundary from "../error/ErrorBoundary";
import CustomModal from "./CustomModal";
import ModalNewsletter from "../home/ModalNewsletter";
import RequestAQuoteFormModal from "./RequestAQuoteFormModal";
import SearchBarModal from "./SearchBarModal";

const NEWSLETTER_MODAL_CLOSED_KEY = "newsletterModalClosed";

const Layout: FC<{
    children: ReactNode;
}> = ({ children }) => {
    const [showModal, setShowModal] = useState(false);
    const [showSearchModal, setShowSearchModal] = useState(false);

    // Debug: log quando showModal cambia
    useEffect(() => {
        if (process.env.NODE_ENV === "development") {
            // eslint-disable-next-line no-console
            console.log("Newsletter modal - showModal changed to:", showModal);
        }
    }, [showModal]);
    const requestAQuote = useStore(state => state.requestAQuoteState);
    const dispatchRequestAQuote = useStore(
        state => state.dispatchRequestAQuote
    );
    const router = useRouter();
    const [emailSubscriptionNewsletter, setEmailSubscriptionNewsletter] =
        useState("");
    const email = useStore(state => state.userState.userDetail.email);

    const { data: subscribed } = useQuery({
        queryKey: ["subscribed", emailSubscriptionNewsletter],
        queryFn: () => getSubscribeNewsletter(emailSubscriptionNewsletter),
        enabled: !!emailSubscriptionNewsletter,
        refetchOnWindowFocus: false,
    });

    const { data: newsletterFormMetadata } = useQuery({
        queryKey: ["newsletterFormMetadata"],
        queryFn: () => getNewsletterFormService(),
        refetchOnWindowFocus: false,
    });

    useEffect(() => {
        setEmailSubscriptionNewsletter(
            localStorage?.getItem("emailSubscriptionNewsletter") as string
        );
    }, [email]);

    // Funzione helper per controllare se la modale può essere mostrata
    const canShowModal = (): boolean => {
        try {
            const isClosed =
                sessionStorage.getItem(NEWSLETTER_MODAL_CLOSED_KEY) === "true";
            return !isClosed;
        } catch (error) {
            // Se c'è un errore con sessionStorage, mostra comunque la modale
            return true;
        }
    };

    // Funzione helper per gestire il trigger OPEN_WEBSITE
    const setupOpenWebsiteTrigger = (
        timer: number | undefined,
        cleanupFunctions: (() => void)[]
    ) => {
        const delay = (timer ?? 0) * 1000;

        if (process.env.NODE_ENV === "development") {
            // eslint-disable-next-line no-console
            console.log(
                "Newsletter modal - Setting timeout for OPEN_WEBSITE trigger, delay:",
                delay,
                "ms"
            );
        }

        const timeout = setTimeout(() => {
            if (canShowModal()) {
                setShowModal(true);
            } else if (process.env.NODE_ENV === "development") {
                // eslint-disable-next-line no-console
                console.log(
                    "Newsletter modal - Timeout fired, modal was closed, not showing"
                );
            }
        }, delay);

        cleanupFunctions.push(() => clearTimeout(timeout));
    };

    // Funzione helper per gestire il trigger SCROLL_TO_UP
    const setupScrollToUpTrigger = (cleanupFunctions: (() => void)[]) => {
        const handleScroll = () => {
            if (window.scrollY === 0 && canShowModal()) {
                setShowModal(true);
            }
        };
        window.addEventListener("scroll", handleScroll);
        cleanupFunctions.push(() =>
            window.removeEventListener("scroll", handleScroll)
        );
    };

    // Funzione helper per gestire il trigger INACTIVITY
    const setupInactivityTrigger = (
        timer: number | undefined,
        cleanupFunctions: (() => void)[]
    ) => {
        let timeout: ReturnType<typeof setTimeout>;
        const resetTimer = () => {
            clearTimeout(timeout);
            timeout = setTimeout(() => {
                if (canShowModal()) {
                    setShowModal(true);
                }
            }, (timer ?? 0) * 1000);
        };

        const events = [
            "mousemove",
            "mousedown",
            "click",
            "scroll",
            "keydown",
            "touchstart",
        ];

        events.forEach(event => window.addEventListener(event, resetTimer));
        resetTimer();

        cleanupFunctions.push(() => {
            clearTimeout(timeout);
            events.forEach(event =>
                window.removeEventListener(event, resetTimer)
            );
        });
    };

    // Funzione helper per gestire il trigger URL
    const setupUrlTrigger = (
        urls: { path: string; timer: number }[],
        currentPath: string,
        cleanupFunctions: (() => void)[]
    ) => {
        const urlTrigger = urls.find(url => currentPath.includes(url.path));
        if (urlTrigger) {
            const delay = (urlTrigger.timer ?? 0) * 1000;
            const timeout = setTimeout(() => {
                if (canShowModal()) {
                    setShowModal(true);
                }
            }, delay);
            cleanupFunctions.push(() => clearTimeout(timeout));
        }
    };

    // Funzione helper per verificare se la modale è chiusa
    const checkIfModalClosed = (): boolean => {
        try {
            return (
                sessionStorage.getItem(NEWSLETTER_MODAL_CLOSED_KEY) === "true"
            );
        } catch (error) {
            if (process.env.NODE_ENV === "development") {
                // eslint-disable-next-line no-console
                console.warn("Error accessing sessionStorage:", error);
            }
            return false;
        }
    };

    // Funzione helper per gestire i trigger
    const setupTriggers = (
        trigger: {
            type: ENewsletterTriggerOption;
            timer?: number;
            urls?: { path: string; timer: number }[];
        },
        cleanupFunctions: (() => void)[]
    ) => {
        switch (trigger.type) {
            case ENewsletterTriggerOption.OPEN_WEBSITE:
                setupOpenWebsiteTrigger(trigger.timer, cleanupFunctions);
                break;
            case ENewsletterTriggerOption.CLOSE_WEBSITE:
                // DA CAPIRE - trigger disattivato per ora
                break;
            case ENewsletterTriggerOption.SCROLL_TO_UP:
                setupScrollToUpTrigger(cleanupFunctions);
                break;
            case ENewsletterTriggerOption.INACTIVITY:
                setupInactivityTrigger(trigger.timer, cleanupFunctions);
                break;
            case ENewsletterTriggerOption.URL:
                if (trigger.urls) {
                    setupUrlTrigger(
                        trigger.urls,
                        router.asPath,
                        cleanupFunctions
                    );
                }
                break;
            default:
                break;
        }
    };

    useEffect(() => {
        // Se non ci sono ancora i metadata, aspetta
        if (!newsletterFormMetadata) {
            if (process.env.NODE_ENV === "development") {
                // eslint-disable-next-line no-console
                console.log("Newsletter modal - Waiting for metadata...");
            }
            return undefined;
        }

        const trigger = newsletterFormMetadata?.trigger;
        const isSubscribed =
            subscribed !== undefined && subscribed?.subscription === true;
        const isModalClosed = checkIfModalClosed();

        if (process.env.NODE_ENV === "development") {
            // eslint-disable-next-line no-console
            console.log(
                "Newsletter modal - isModalClosed:",
                isModalClosed,
                "isSubscribed:",
                isSubscribed,
                "subscribed:",
                subscribed,
                "trigger:",
                trigger?.type,
                "timer:",
                trigger?.timer
            );
        }

        const cleanupFunctions: (() => void)[] = [];

        // Early returns per condizioni che impediscono l'attivazione dei trigger
        if (isSubscribed || isModalClosed) {
            setShowModal(false);
            if (isModalClosed && process.env.NODE_ENV === "development") {
                // eslint-disable-next-line no-console
                console.log(
                    "Newsletter modal - Already closed in this session, skipping timeout"
                );
            }
            return () => {
                cleanupFunctions.forEach(cleanup => cleanup());
            };
        }

        if (!trigger?.type) {
            if (process.env.NODE_ENV === "development") {
                // eslint-disable-next-line no-console
                console.log("Newsletter modal - No trigger configured");
            }
            return () => {
                cleanupFunctions.forEach(cleanup => cleanup());
            };
        }

        setupTriggers(trigger, cleanupFunctions);

        return () => {
            cleanupFunctions.forEach(cleanup => cleanup());
        };
        // Rimuovo router.asPath dalle dipendenze per evitare che il timeout venga cancellato ad ogni navigazione
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [newsletterFormMetadata, subscribed]);

    // Funzione helper per aprire la modale (controlla il flag prima)
    const handleOpenModal = () => {
        try {
            const isClosed =
                sessionStorage.getItem(NEWSLETTER_MODAL_CLOSED_KEY) === "true";
            if (isClosed) {
                if (process.env.NODE_ENV === "development") {
                    // eslint-disable-next-line no-console
                    console.log(
                        "Newsletter modal - Cannot open, already closed in this session"
                    );
                }
                return;
            }
            setShowModal(true);
        } catch (error) {
            // Se c'è un errore con sessionStorage, mostra comunque la modale
            setShowModal(true);
        }
    };

    // Funzione helper per chiudere la modale e salvare il flag in sessionStorage
    // Compatibile con Dispatch<SetStateAction<boolean>>
    const handleCloseModal: Dispatch<SetStateAction<boolean>> = value => {
        // Se value è una funzione, chiamala con lo stato corrente, altrimenti usa il valore passato
        const newValue = typeof value === "function" ? value(showModal) : value;
        const wasVisible = showModal === true;
        setShowModal(newValue);
        // Salva il flag SOLO se la modale era visibile E viene chiusa (false)
        // Questo evita di salvare il flag se la modale non è mai apparsa
        if (wasVisible && newValue === false) {
            try {
                sessionStorage.setItem(NEWSLETTER_MODAL_CLOSED_KEY, "true");
                if (process.env.NODE_ENV === "development") {
                    // eslint-disable-next-line no-console
                    console.log(
                        "Newsletter modal - Flag saved to sessionStorage, modal will not appear again this session"
                    );
                }
            } catch (error) {
                if (process.env.NODE_ENV === "development") {
                    // eslint-disable-next-line no-console
                    console.warn("Error saving to sessionStorage:", error);
                }
            }
        } else if (
            newValue === false &&
            !wasVisible &&
            process.env.NODE_ENV === "development"
        ) {
            // eslint-disable-next-line no-console
            console.log(
                "Newsletter modal - Modal was not visible, not saving flag"
            );
        }
    };

    return (
        <>
            <HeadMeta />
            <MDBContainer className="ps-0 pe-0" fluid>
                <CustomModal
                    displayModal={showModal}
                    setDisplayModal={() => handleCloseModal(false)}
                    noTitle
                    classNameBody="ps-lg-0"
                    classNameContent="text-center w-100 border-0 px-2 px-lg-5 position-relative"
                >
                    <ModalNewsletter
                        metadata={newsletterFormMetadata}
                        setShowModal={handleCloseModal}
                    />
                </CustomModal>
                <RequestAQuoteFormModal
                    show={requestAQuote.isOpen}
                    id={requestAQuote.idTour}
                    title={requestAQuote.title}
                    activities={requestAQuote.activities}
                    setShow={() =>
                        dispatchRequestAQuote({ type: "RESET_REQUEST_A_QUOTE" })
                    }
                />

                <NavBarLayout
                    setShowModal={handleOpenModal}
                    setShowSearchModal={setShowSearchModal}
                    bannerLabel={
                        newsletterFormMetadata?.newsletterBannerLabel ?? ""
                    }
                />
                <SearchBarModal
                    show={showSearchModal}
                    setShow={setShowSearchModal}
                />
                <ErrorBoundary>{children}</ErrorBoundary>
                <FooterLayout />
            </MDBContainer>
        </>
    );
};

export default Layout;
